import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

// Initialize Sentry for error tracking
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring — sample 20% of transactions (errors are still
    // captured at 100% regardless; this only trims performance-trace volume/cost
    // ahead of opening the app up beyond the household).
    tracesSampleRate: 0.2,
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    environment: import.meta.env.MODE,
    
    // Filter out certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      // Random plugins/extensions
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Network errors we can't control
      'NetworkError',
      'Failed to fetch',
      // ResizeObserver - safe to ignore
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
    
    // Add custom data to all events
    beforeSend(event, _hint) {
      // Don't send errors in development (extra safety)
      if (import.meta.env.DEV) {
        return null
      }
      
      // Add custom context
      if (event.contexts) {
        event.contexts.app = {
          version: '1.0.0-beta',
          theme: localStorage.getItem('symphony-theme') || 'nordic',
        }
      }
      
      return event
    },
  })
}

// Handle Vite CSS preload errors gracefully — don't crash the entire app
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault()
})

// One design system. "Your place" (Settings) is the only theme choice; the
// old Appearance switch (Kinetic Clarity) was retired 2026-09-23, so a saved
// 'kinetic' preference no longer strands anyone on a look they can't leave.
await import('./index.css')
// The layout system's shared shapes, after index.css so they settle it
// (docs/design-system/LAYOUT-SYSTEM.md).
await import('./styles/layout-system.css')
// The conversational canvas's shared forms, drawn on the same tokens.
await import('./styles/canvas.css')
await import('./styles/canvas-today.css')
await import('./styles/canvas-plan.css')
// The deck: one card at a time (Friday session).
await import('./styles/deck.css')
await import('./styles/choose.css')
await import('./styles/canvas-kiosk.css')
await import('./styles/kiosk-v1.css')
// Math of the day's stage, with the wall's other styles (a component-level
// import shipped as its own file, which the kitchen wall never applied).
await import('./styles/kiosk-math.css')
// The house's power (the wall's Energy stage).
await import('./styles/kiosk-energy.css')
// All of today · This weekend on the wall.
await import('./styles/kiosk-agenda.css')
// The kept school digest on the wall (More → School digest).
await import('./styles/kiosk-digest.css')
// A kid's tablet (/kid).
await import('./styles/kid-kiosk.css')
await import('./styles/canvas-routines.css')
// Discussions with someone: the top-bar count, the phone's Waiting on you.
await import('./styles/discussions.css')

// Apply the cached place theme before first paint so there's no color flash;
// PlaceProvider owns it (and syncs with the DB) once React mounts.
const savedPlace = localStorage.getItem('symphony-place')
if (savedPlace && savedPlace !== 'cabin' &&
    ['urban', 'small-city', 'mountain-town', 'farm'].includes(savedPlace)) {
  document.documentElement.dataset.place = savedPlace
}

// Large text, likewise, before first paint and on every page. It used to be
// applied only by the Settings page's hook, so a reload anywhere else quietly
// dropped it (2026-09-23).
try {
  if (localStorage.getItem('symphony-large-text') === 'true') document.documentElement.classList.add('large-text')
} catch { /* storage unavailable: default size */ }

import { Suspense, lazy } from 'react'
import { CalendarCallback } from './pages/CalendarCallback'
import { isDesktopShell } from './lib/desktop'
import { startRealtimeKeepAlive } from './lib/realtime/keepAlive'
import { JoinHousehold } from './components/JoinHousehold'
import { GoogleCalendarProvider } from './hooks/useGoogleCalendar'
import { DomainProvider } from './hooks/useDomain'
import { DomainGateProvider } from './components/domain/DomainGate'
import { PlaceProvider } from './hooks/usePlace'
import { Shell } from './shell/Shell'
import { AuthGate } from './components/auth/AuthGate'

// A tab that reloads while a deploy lands can be handed an index.html whose
// chunks are gone (seen 2026-10-15: a blank page until a hard reload). Vite
// reports the failed import; reload once, with the new index, instead of
// leaving the person a white screen. Guarded so it cannot loop.
window.addEventListener('vite:preloadError', (event) => {
  let already = false
  try { already = sessionStorage.getItem('symphony.reloadedForChunk') === '1'; if (!already) sessionStorage.setItem('symphony.reloadedForChunk', '1') } catch { /* no storage */ }
  if (already) return
  event.preventDefault()
  window.location.reload()
})
import { FirstRunGate } from './components/setup/FirstRunGate'
import { OnboardingFlow } from './components/setup/OnboardingFlow'
import { ErrorBoundary } from './components/ErrorBoundary'

// Quick-capture window for the Mac shell (desktop/): a frameless Tauri window
// loads /capture via the global hotkey. Lazy — browsers never fetch it.
const CapturePage = lazy(() => import('./desktop/CapturePage').then((m) => ({ default: m.CapturePage })))
// Plan-from-paper phone hand-off: opened from the desktop's QR code.
const PhonePaperPage = lazy(() => import('./components/capture/PhonePaperPage').then((m) => ({ default: m.PhonePaperPage })))
// Design preview for the person-lane wall. Ungated like the kiosk routes so it
// can be opened on the Pi without a session; carries no live data.
const WallV2LanePreview = lazy(() => import('./components/wall-v2/WallV2LanePreview').then((m) => ({ default: m.WallV2LanePreview })))
const KioskScenePreview = lazy(() => import('./components/wall-v2/activity/KioskScenePreviewRoute'))
const ChoiceFixture = import.meta.env.DEV ? lazy(() => import('./dev/choiceFixture')) : null
const KidKiosk = lazy(() => import('./components/kids/KidKioskRoute'))
const KidKioskPreview = lazy(() => import('./components/kids/KidKioskPreviewRoute'))

// P5 cutover (gated). /, /today, /inbox, /task/:id route to the new Shell-mounted
// TasksApp when the flag is enabled, otherwise to legacy App.tsx. Both paths now
// share the same auth gate (AuthGate): App wraps itself in it, and
// the Shell mounts here are wrapped via `tasksElement`. This is what unblocks
// flipping the flag default-ON — previously the Shell rendered ungated and the
// auth-form e2e specs at `/` regressed (e2e/app.spec.ts). Flip locally for
// parallel-path testing:
//   localStorage.setItem('symphony.useNewTasks', '1'); location.reload()  // shell-mounted
//   localStorage.removeItem('symphony.useNewTasks'); location.reload()    // back to legacy (default)
//
// /tasks-new/* always routes to Shell regardless of the flag (planned to
// remove together with the legacy mounts once the cutover completes).
// Cutover complete (2026-06): the Shell owns the entire app. App.tsx is retired.
// The Shell MUST be mounted at a root-level splat
// (`/*`), never at exact paths like `/today`. The Shell renders descendant
// <Routes> (ShellRoutes -> TasksApp), and TasksApp matches segment-named child
// routes (`today`, `inbox`, `task/:id`). React Router only passes the remaining
// path to a descendant <Routes> when the parent route ends in `*`; mounting at
// exact `/today` consumes the segment and leaves nothing to match, so Today
// rendered blank. See src/shell/cutoverRouting.test.tsx for the repro + fix.
// Inside the Tauri Mac shell (desktop/), scope the native-feel CSS pass
// (cursors, selection, overscroll — see index.css tail) to the whole document.
if (isDesktopShell()) {
  document.documentElement.classList.add('desktop-shell')
}

// Bring the realtime socket back when it dies. Module scope, not a component:
// it watches the one shared socket for the whole app, so it should not be tied
// to any tree that can unmount. See lib/realtime/keepAlive.ts.
startRealtimeKeepAlive()

// A brand-new account sees the one-screen household setup before the Shell.
const cutoverShell = <AuthGate>{({ user }) => <FirstRunGate user={user}><Shell /></FirstRunGate>}</AuthGate>

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <PlaceProvider>
      <DomainProvider>
      <DomainGateProvider>
        <BrowserRouter>
          <GoogleCalendarProvider>
            <Routes>
              <Route path="/getting-started" element={<AuthGate>{({ user }) => <OnboardingFlow key={user.id} user={user} />}</AuthGate>} />
              {/* /, /today, /inbox, /task/:id are served by the Shell via the
                  /* catch-all below (explicit routes here win by specificity). */}
              <Route path="/goals/*" element={cutoverShell} />
              <Route path="/choice/*" element={cutoverShell} />
              {/* Dev only: the comparison page with fixed data, outside AuthGate. */}
              {import.meta.env.DEV && <Route path="/dev/choice-fixture" element={<Suspense fallback={null}>{ChoiceFixture && <ChoiceFixture />}</Suspense>} />}
              {/* /projects retired from the UI (2026-09-02): "Projects" read as
                  GTD jargon and muddied the household-OS pitch. HIDE THE NOUN,
                  KEEP THE DATA — the table, useProjects, types/project.ts and
                  ProjectsList/ProjectView all stay; only the way in goes.
                  Redirected rather than deleted, for the same reason /jobs and
                  /us are: stale bookmarks and the Mac shell's restored route. */}
              <Route path="/projects/*" element={<Navigate to="/today" replace />} />
              <Route path="/routines/*" element={cutoverShell} />
              {/* /meds withheld — see the note in Sidebar.tsx. Redirected
                  rather than simply deleted: unmounting the app alone left the
                  path falling through to the catch-all, which rendered a
                  half-drawn shell with an empty body. A stale bookmark, or the
                  Mac shell restoring its last route, would land there. */}
              <Route path="/meds/*" element={<Navigate to="/today" replace />} />
              <Route path="/contacts/*" element={cutoverShell} />
              <Route path="/family/*" element={cutoverShell} />
              {/* The Michael agent page went with Open Brain (2026-10-10). */}
              <Route path="/agent/*" element={<Navigate to="/today" replace />} />
              {/* The legacy /wall app is deleted; with no app registered the
                  shell rendered chrome around an empty body. Send stale kiosk
                  bookmarks to the current board instead. */}
              <Route path="/wall/*" element={<Navigate to="/wall-v2" replace />} />
              <Route path="/wall-v2/*" element={<Shell />} />
              <Route path="/wall-lanes" element={<Suspense fallback={null}><WallV2LanePreview /></Suspense>} />
              {/* Dev only: every approved kiosk board with fixed data — no reads,
                  writes or calls — for design review screenshots. */}
              {import.meta.env.DEV && <Route path="/kiosk-preview" element={<Suspense fallback={null}><KioskScenePreview /></Suspense>} />}
              {/* A kid's tablet: paired for one kid in Settings → Displays. A
                  grown-up can open /kid/<member id>?layout=trail|list. */}
              <Route path="/kid" element={<Suspense fallback={null}><KidKiosk /></Suspense>} />
              <Route path="/kid/:memberId" element={<Suspense fallback={null}><KidKiosk /></Suspense>} />
              {/* The plural is the natural thing to type on a tablet; it
                  opened an empty Today (Scott, 2026-10-27). */}
              <Route path="/kids" element={<Navigate to="/kid" replace />} />
              {import.meta.env.DEV && <Route path="/kid-preview" element={<Suspense fallback={null}><KidKioskPreview /></Suspense>} />}
              {/* /jobs and /us retired (pare-down 2026-09-01): the job pipeline
                  moved out of Symphony (the vault owns job search) and the Us
                  surface was axed. Redirect, don't 404 — stale bookmarks and
                  the Mac shell's restored routes may still land here. */}
              <Route path="/jobs/*" element={<Navigate to="/today" replace />} />
              <Route path="/us/*" element={<Navigate to="/today" replace />} />
              <Route path="/tasks-new/*" element={cutoverShell} />
              {/* /morning and /bedtime retired — KidDayView (opened from the
                  wall board) replaced them. Redirected rather than deleted:
                  the Pi or old bookmarks may still hit these paths, and a
                  404'd kiosk is worse than a redirect. */}
              <Route path="/morning/*" element={<Navigate to="/wall-v2" replace />} />
              <Route path="/bedtime/*" element={<Navigate to="/wall-v2" replace />} />
              <Route path="/meals/*" element={cutoverShell} />
              <Route path="/home/*" element={cutoverShell} />
              <Route path="/settings/*" element={cutoverShell} />
              <Route path="/history/*" element={cutoverShell} />
              <Route path="/lists/*" element={cutoverShell} />
              <Route path="/capture" element={<Suspense fallback={null}><CapturePage /></Suspense>} />
              <Route path="/join/:token" element={<JoinHousehold />} />
              <Route
                path="/paper/phone/:id"
                element={<AuthGate>{({ user }) => <Suspense fallback={null}><PhonePaperPage user={user} /></Suspense>}</AuthGate>}
              />
              <Route path="/calendar-callback" element={<CalendarCallback />} />
              {/* The Shell owns the cutover paths (/, /today, /inbox, /task/:id)
                  and any unmatched path. Explicit routes above win by specificity. */}
              <Route path="/*" element={cutoverShell} />
            </Routes>
          </GoogleCalendarProvider>
        </BrowserRouter>
      </DomainGateProvider>
      </DomainProvider>
      </PlaceProvider>
    </ErrorBoundary>
  </StrictMode>,
)
